import { DocumentationWithExternalLink } from '../../../../Definitions/Definition/Drawer/Content/DocumentationWithExternalLink';
import { UiBlockType } from '../../../types';

export function FileConversionBlockDocumentation() {
  return (
    <DocumentationWithExternalLink
      blockType={UiBlockType.FileConversion}
      documentationMarkdown={FILE_CONVERSION_DOCUMENTATION_MARKDOWN}
    />
  );
}

const FILE_CONVERSION_DOCUMENTATION_MARKDOWN = `
## Transform File

## What does this block do?

The Transform File block performs file operations: converting files between formats, combining multiple PDFs into one, splitting PDFs into multiple files, or extracting specific pages from a PDF into a single new file. For cross-format conversion, splitting, and extracting pages, the block accepts a single file reference. For PDF combination, the block accepts a list of file references.

Use the **Operation** selector to choose what this block does.

## Operations

### Convert File

Converts files between formats. Pick one of the formats below — the table summarizes what each accepts as input and produces as output.

| Format | Input | Output |
|---|---|---|
| **Base64** | A file reference (≤ 50 MB) | A Base64-encoded string |
| **PDF** | Most common image formats (TIFF, PNG, JPEG, GIF, BMP, WebP, …) or already-PDF | A \`flowDocument:{id}\` file reference to a \`.pdf\` |

**Formats:**

- **Base64** — Converts a file to a Base64-encoded string. Max file size: 50 MB. Returns the encoded bytes inline, so size is bounded by the workflow engine's payload limits.
- **PDF** — Converts most common image formats (TIFF, PNG, JPEG, GIF, BMP, WebP, …) to PDF. If the input is already a PDF, the original file reference is returned as-is.
  - **Use when:** You need to convert image files to PDF for standardization, preparing documents for PDF-based workflows, or archiving images in a consistent document format.
  - **This operation actively produces a PDF.** Chain it after any block that yields a non-PDF file (such as **Generate File → Base64 to File**) when downstream blocks require PDF input.
- **Base64 to File** — _Deprecated. Use the **Generate File** block's **Base64 to File** mode instead._ Converts a Base64-encoded string into a stored file and returns a file reference. **Note:** the resulting file's extension follows the decoded bytes (it preserves the source format and does **not** force conversion to any specific format). To convert the output to PDF, chain a **Convert File → PDF** step after.

**Output:** A single string — either a Base64 string or a \`flowDocument:{id}\` file reference.

### Split PDF

Splits a PDF document into multiple files. Max source file size: 225 MB.

Choose a **Split by** mode:

- **Range** — Split by explicit page ranges. Enter comma-separated page numbers and/or inclusive ranges (1-indexed).
  - \`1-5,6-12,13-20\` → 3 files: pages 1–5, 6–12, 13–20
  - \`1,2,3\` → 3 files: one page each
  - \`1-3,5,7-9\` → 3 files: pages 1–3, page 5, pages 7–9
  - Overlapping ranges (e.g., \`5-10,3-7\`) are allowed — each range produces an independent output file. Heavily overlapping ranges that would push the **total** output well above the source file size will be rejected; use smaller or non-overlapping ranges if you hit this.

- **Every X page** — Split every N pages. Enter a positive whole number. A 22-page PDF split every 5 pages produces 5 files: pages 1–5, 6–10, 11–15, 16–20, 21–22.

Both the **Extract Range** and **Pages per file** fields support template syntax for dynamic values from upstream blocks (e.g., \`{{categorizePages.data.pageRanges}}\` or \`{{getPageCount.data.count}}\`).

**Output:** \`{ fileReferences: ["flowDocument:id1", "flowDocument:id2", ...] }\`

**Using with Iterator:** To process each split file individually, add an Iterator block downstream and set its items to: \`{{transformFile.data.fileReferences}}\` (replace \`transformFile\` with your block's label). Each iteration receives a single \`flowDocument:{id}\` string.

### Combine PDFs

Merges multiple PDF documents into a single PDF and returns a file reference to the combined result.

**What it does:**
- Accepts a JSON array of file references (e.g., \`["flowDocument:abc123", "document:def456"]\`)
- Downloads each file and validates it is a valid PDF
- Merges all pages in order into a single PDF document
- Stores the result in Google Cloud Storage
- Returns a \`flowDocument:{id}\` file reference

**Input:**
- Uses the **File References** field — a JSON array of file reference strings
- Supports template syntax: the entire field can be a template expression that resolves to a JSON array (e.g., \`{{myBlock.data.fileReferences}}\`)
- Pages are combined in the order the references appear in the array

**Output:** A single \`flowDocument:{id}\` file reference pointing to the merged PDF.

**Use when:** You need to merge multiple PDF documents or assemble a packet from multiple file references produced by upstream blocks.

**Limitations:**
- All files must be valid PDF documents — non-PDF files will cause a non-retryable error
- The field must resolve to a valid JSON array of strings at runtime

### Extract Pages

Extracts specific pages from a PDF document into a single new PDF. Enter the page numbers you want to keep, which can be individual pages, ranges, or a combination.

**Page Numbers syntax:**
- Individual pages: \`1,2,3,5\` → single PDF with pages 1, 2, 3, and 5
- Ranges: \`1-5,10-15\` → single PDF with pages 1–5 and 10–15
- Mixed: \`1,3-5,7,10-12\` → single PDF with pages 1, 3, 4, 5, 7, 10, 11, and 12
- Duplicates are automatically removed while preserving the first occurrence

**Page Numbers** field supports template syntax for dynamic values from upstream blocks (e.g., \`{{getPageList.data.pages}}\`).

**Output:** A single file reference string: \`flowDocument:{id}\`

**Common use cases:**
- Extracting specific pages based on document classification
- Keeping only relevant pages from a larger document
- Creating a subset document from non-consecutive pages

## File Reference

The **PDF File** / **File reference** field accepts a specially formatted string that references a file known to the system.

**Format:** \`{type}:{identifier}\`

**Supported file reference types:**

- \`document:{documentId}\` - References a document stored in the system
- \`externalDocumentPacket:{packetId}\` - References an external document packet
- \`fhirDocument:{documentPath}\` - References a FHIR document
- \`flowDocument:{flowDocumentId}\` - References a flow document (commonly used for files created within flows)
- \`humanTaskAsset:{assetPath}\` - References a human task asset
- \`notableClassicJobScreenshot:{screenshotPath}\` - References a screenshot of a Notable Classic Job execution
- \`patientUpload:{uploadId}\` - References a patient upload

You can reference files from upstream blocks using template syntax. For example: \`{{getDocument.data.fileReference}}\`

## Blocks that accept file references

The following blocks can accept file references as input:

1. **Transform File** - Accepts a single file reference when required for Convert File with Base64/PDF formats, Split PDF, and Extract Pages, a Base64 payload for Convert File with Base64 to File format, or a JSON array of file references for Combine PDFs
   - Field: \`fileReference\` (for Convert File operation with Base64/PDF formats, Split PDF operation, and Extract Pages operation), \`base64Payload\` (for Base64 to File format), or \`fileReferences\` (for Combine PDFs operation)
2. **LLM Prompt** - Accepts one or multiple file references (optional)
3. **OCR** - Accepts a single file reference (optional)
4. **Fax** - Accepts a single file reference (required)
   - Field: \`fileReference\`
   - Use: Send a file via fax

5. **Generate Document (Combine PDF mode)** - Accepts multiple file references (required)
   - Field: \`fileReferences\` (JSON array as string)
   - Use: Combine multiple PDF files into a single PDF
   - **⚠️ DEPRECATED:** Use Transform File > Combine PDFs operation instead for new flows

6. **Human Task (File work context)** - Accepts a single file reference (required)
   - Field: \`fileReference\` (in work context configuration)
   - Use: Display a file for human review in a task

**Note:** All file references use the format \`{type}:{identifier}\` and can reference outputs from upstream blocks using template syntax like \`{{blockName.data}}\`.

## Other Useful Tips

**Template Syntax:**
- Use \`{{blockName.data}}\` to reference the output from upstream blocks
- For blocks that return objects with a \`fileReference\` property, use \`{{blockName.data.fileReference}}\`
- The Transform File block returns the value directly, so use \`{{transformFile.data}}\` (not \`.fileReference\`)

**Performance Considerations:**
- Source file size limits per operation:
  - Convert to Base64: 50 MB
  - Split PDF: 225 MB
  - Convert to PDF, Convert to Base64 to File, Combine PDFs, Extract Pages: 30 MB each
- Large image files may take longer to process for PDF conversion
- PDF conversion is more resource-intensive than Base64 encoding
- Consider file size when designing flows that process many files

**File Reference Best Practices:**
- Most commonly used types: \`flowDocument:\` (for files created in flows) and \`externalDocumentPacket:\` (for external documents)
- File references are automatically handled by downstream blocks - no manual retrieval needed

**Testing:**
- Test with sample files before deploying to production
- Verify the output format matches your downstream block's expectations
- For Base64, check that the string length is manageable for your use case

## Error Conditions

The block will fail with non-retryable errors in the following situations:

**Invalid File Reference:**
- Error: \`Invalid file reference\` or \`Invalid file reference type: {type}\`
- Solution: Ensure the file reference follows the format \`{type}:{identifier}\`

**File Not Found:**
- Error: \`Unable to download file: {error message}\`
- Solution: Verify the file reference is correct and the file exists

**Unsupported Format:**
- Error: \`Format {format} is unsupported\`
- Cause: An invalid format value was provided
- Solution: Use only \`Base64\`, \`Pdf\`, or \`Base64ToFile\` as format values

**File Size Exceeded:**
- Error: File download fails or exceeds limit
- Cause: File is larger than the per-operation ceiling (50 MB for Convert → Base64; 225 MB for Split PDF; 30 MB for all others — Convert → PDF, Convert → Base64 to File, Combine PDFs, and Extract Pages)
- Solution: Use a smaller file, or for very large PDFs use the Split PDF operation first to break the document into smaller pieces

**Total Split Output Size Exceeded (Split PDF):**
- Error: \`Total split output bytes ... would exceed the maximum allowed\`
- Cause: The combined size of the split output PDFs would exceed the per-activity cumulative-output limit. This is normally triggered by heavily overlapping page ranges (each overlapping range becomes an independent output file) or by inputs that pdf-lib re-serializes to a noticeably larger size than the source.
- Solution: Use smaller or non-overlapping page ranges, or split the document across multiple Transform File invocations

**Image Conversion Failures (PDF format):**
- Error: \`Failed to convert to PDF: {error message}\`
- Solution: Verify the image file is not corrupted and is a supported format

**Invalid Base64 Payload (Base64ToFile format):**
- Error: \`Invalid base64 payload\` or decoding error
- Cause: The base64Payload field contains an invalid or malformed Base64 string
- Solution: Verify the Base64 string is properly encoded and contains valid data

**Invalid File References (Combine PDFs operation):**
- Error: \`fileReferences must resolve to a valid JSON array of file reference strings\`
- Cause: The field did not resolve to a JSON array of strings after template replacement
- Solution: Ensure the field contains a valid JSON array (e.g., \`["flowDocument:abc", "flowDocument:def"]\`) or a template expression that evaluates to one

**Non-PDF Input (Combine PDFs operation):**
- Error: \`File at index {n} is not a valid PDF: {ref}\`
- Cause: One of the files in the array is not a valid PDF document
- Solution: Verify all referenced files are valid PDFs

**Invalid Page Ranges (Split by Range):**
- Error: \`Invalid page range\` or \`exceeds document page count\`
- Solution: Ensure ranges use the format \`1-5,6-12\` with valid 1-indexed page numbers

**Invalid Page Count (Split by Every X page):**
- Error: \`Pages per file must be a positive whole number\`
- Solution: Provide a whole number greater than 0

**Failed to Load PDF (Split operations):**
- Error: \`Failed to load PDF document\`
- Solution: Verify the input file is a valid, uncorrupted PDF document

**Empty Page Numbers (Extract Pages operation):**
- Error: \`pageNumbers must not be empty\` or \`pageNumbers must contain at least one valid page\`
- Solution: Provide at least one page number or range (e.g., \`1,3-5,7\`)

**Invalid Page Numbers (Extract Pages operation):**
- Error: \`Invalid page range\` or \`must be between 1 and {totalPages}\`
- Solution: Use the format \`1,2,3,5\` or \`1,3-5,7\` with valid 1-indexed page numbers within the document's page count
`;
